/**
 * I segnali acustici del banco.
 *
 * Al banco l'operatore ha il transponder in una mano e la fila davanti: guardare
 * lo schermo per sapere com'è andata è il passaggio che si vuole togliere. Ogni
 * esito ha un suono diverso, riconoscibile senza guardare.
 *
 * I suoni sono **sintetizzati**, non file audio: tre note generate al momento
 * pesano zero, non c'è niente da precaricare nel service worker e niente che
 * possa non essere ancora scaricato quando serve — cioè proprio durante una
 * gara, con la rete del palazzetto che va e viene.
 *
 * Regola non negoziabile: un suono non deve **mai** far fallire l'operazione.
 * Tutto è dentro `try/catch`, e se l'audio non è disponibile si prosegue in
 * silenzio.
 */

/** Cosa è appena successo, dal punto di vista di chi ha in mano il lettore. */
export type SoundCue = 'ok' | 'already_returned' | 'unknown_code';

type Note = {
  /** Frequenza in hertz. */
  frequency: number;
  /** Durata in millisecondi. */
  duration: number;
  /** Ritardo dall'inizio, in millisecondi. */
  delay?: number;
  /** Volume relativo alla nota più forte del segnale, da 0 a 1. */
  gain?: number;
};

/**
 * La forma di ogni segnale. Sono scelte di orecchio, non di matematica:
 *
 * - `ok` è una nota alta e secca: succede decine di volte di fila, non deve
 *   stancare né coprire le voci.
 * - `already_returned` scende di due note: si sente che qualcosa non è andato
 *   come previsto, ma non è un allarme.
 * - `unknown_code` è più basso e ripetuto: è l'unico caso in cui serve davvero
 *   che l'operatore alzi lo sguardo, perché c'è una decisione da prendere.
 */
const CUES: Record<SoundCue, readonly Note[]> = {
  ok: [{ frequency: 880, duration: 70, gain: 1 }],
  already_returned: [
    { frequency: 660, duration: 90, gain: 1 },
    { frequency: 440, duration: 140, delay: 90, gain: 1 },
  ],
  unknown_code: [
    { frequency: 440, duration: 80, gain: 0.9 },
    { frequency: 440, duration: 80, delay: 110, gain: 0.9 },
    { frequency: 440, duration: 110, delay: 220, gain: 0.9 },
  ],
};

/** Le note di un esito. Pura: la scelta si può verificare senza audio. */
export function cueNotes(cue: SoundCue): readonly Note[] {
  return CUES[cue];
}

/**
 * La forma d'onda di ogni nota.
 *
 * Era una **sinusoide**, ed era la scelta che rendeva il beep più debole a
 * parità di ampiezza: una sinusoide mette tutta la sua energia in una frequenza
 * sola, e i piccoli altoparlanti di tablet e portatili rendono male proprio le
 * frequenze basse — quelle dei due segnali che contano di più, `already_returned`
 * (660→440 Hz) e `unknown_code` (440 Hz). Col cursore già a fondo scala non
 * restava niente da alzare.
 *
 * Un'onda quadra porta invece le armoniche, che cadono tra 1 e 4 kHz, dove
 * l'orecchio è più sensibile, e **a parità di picco ha energia media doppia**
 * (la sinusoide sta a 0,71 del picco, la quadra a 1). Il segnale buca il rumore
 * della palestra invece di confondercisi.
 */
export const CUE_WAVEFORM: OscillatorType = 'square';

/** Oltre questa frequenza un'onda quadra non si sente più come segnale, ma come ronzio. */
const BUS_CUTOFF_HZ = 5000;

/** Quanto dura la salita di una nota, in secondi. Serve solo a non far sentire il clic. */
const ATTACK_SECONDS = 0.008;

/** Quanto dura la discesa. Tutto il tempo in mezzo è a piena ampiezza. */
const RELEASE_SECONDS = 0.02;

/**
 * Dove comincia e dove finisce la parte piena di una nota, in secondi dall'inizio.
 *
 * Pura ed esportata perché è il secondo punto che si sbaglia, dopo il tetto del
 * volume. Un inviluppo che sale e cala senza tratto pieno passa quasi tutto il
 * tempo sotto la metà del picco: la stessa ampiezza, tenuta meno, si sente meno.
 * Il tratto pieno è quello che rende il beep un segnale e non un tic.
 */
export function noteEnvelope(durationSeconds: number): { attack: number; release: number } {
  const attack = Math.min(ATTACK_SECONDS, durationSeconds / 2);
  const release = Math.max(attack, durationSeconds - RELEASE_SECONDS);
  return { attack, release };
}

export type AudioSettings = {
  enabled: boolean;
  /** Da 0 a 1. */
  volume: number;
};

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = { enabled: true, volume: 0.6 };

const STORAGE_KEY = 'pietrochip.audio';

/**
 * Il volume massimo del segnale più forte: a fondo scala il segnale è a piena
 * ampiezza, e il cursore dell'operatore ha davvero tutta la corsa.
 *
 * Era **un quinto**, con l'idea di risparmiare le orecchie in palestra. Il
 * risultato era l'opposto: a un quinto il beep si perde nel rumore di fondo, e
 * siccome il tetto stava a monte del cursore, alzarlo non cambiava niente — il
 * cursore arrivava al massimo e restava basso lo stesso. Con il tetto a 1 il
 * cursore è l'ampiezza vera, e chi vuole il segnale discreto lo abbassa lui.
 *
 * **1 non è un valore prudente: è il massimo.** Oltre, il campione esce
 * dall'intervallo e viene tagliato — non si sente più forte, si sente
 * distorsione. Quindi, se il segnale risultasse ancora basso a fondo scala, la
 * strada non è alzare questo numero ma togliere energia dove non serve: nel
 * timbro (`CUE_WAVEFORM`) e nella forma dell'inviluppo (`noteEnvelope`).
 */
const MAX_GAIN = 1;

/**
 * L'ampiezza effettiva di un segnale, dal volume scelto dall'operatore.
 *
 * Pura ed esportata perché è il punto che si sbaglia: un tetto basso a monte
 * rende il cursore inutile, e non si sente finché non si è al banco con la fila
 * davanti.
 */
export function effectiveGain(volume: number): number {
  return MAX_GAIN * clampVolume(volume);
}

export function loadAudioSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_AUDIO_SETTINGS;

    const parsed = JSON.parse(raw) as Partial<AudioSettings>;
    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : DEFAULT_AUDIO_SETTINGS.enabled,
      volume: clampVolume(parsed.volume ?? DEFAULT_AUDIO_SETTINGS.volume),
    };
  } catch {
    // Un `localStorage` non disponibile (finestra privata, dati bloccati) non è
    // un motivo per restare senza suoni: si usano le impostazioni predefinite.
    return DEFAULT_AUDIO_SETTINGS;
  }
}

export function saveAudioSettings(settings: AudioSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // La preferenza è una comodità: se non si può ricordare, pazienza.
  }
}

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_AUDIO_SETTINGS.volume;
  return Math.min(1, Math.max(0, value));
}

let context: AudioContext | null = null;
let bus: BiquadFilterNode | null = null;

/**
 * L'`AudioContext`, creato alla prima occorrenza utile.
 *
 * I browser — Safari e iOS in particolare — tengono muto un contesto creato
 * senza un gesto dell'utente. Al banco i gesti non mancano mai: si sta digitando
 * o scannerizzando. La creazione avviene quindi qui, alla prima nota, e non
 * all'avvio dell'applicazione.
 */
function getContext(): AudioContext | null {
  if (context) return context;

  try {
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    context = new Ctor();
    return context;
  } catch {
    return null;
  }
}

/**
 * Il filtro da cui passa tutto prima di arrivare alle casse.
 *
 * Un'onda quadra porta armoniche fino a dieci volte la fondamentale, e quelle
 * sopra i 5 kHz non si sentono più come segnale ma come ronzio. Il taglio tiene
 * la banda che rende il beep riconoscibile — tra 1 e 4 kHz, dove l'orecchio è
 * più sensibile — e toglie la parte che, ripetuta centinaia di volte al banco,
 * diventa il suono che si ricorda invece del segnale.
 */
function getBus(ctx: AudioContext): BiquadFilterNode {
  if (!bus) {
    bus = ctx.createBiquadFilter();
    bus.type = 'lowpass';
    bus.frequency.value = BUS_CUTOFF_HZ;
    bus.Q.value = 0.7;
    bus.connect(ctx.destination);
  }
  return bus;
}

/**
 * Sblocca l'audio, da chiamare su un gesto dell'utente.
 *
 * Su iOS un contesto creato in un gesto ma lasciato `suspended` resta muto anche
 * ai gesti successivi: va ripreso esplicitamente. È idempotente e non costa
 * niente quando il contesto è già attivo.
 */
export function unlockAudio(): void {
  try {
    const ctx = getContext();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  } catch {
    // Niente audio: non è un problema da segnalare all'operatore.
  }
}

/** Riproduce il segnale di un esito. Non lancia mai. */
export function playCue(cue: SoundCue, settings: AudioSettings = loadAudioSettings()): void {
  if (!settings.enabled || settings.volume <= 0) return;

  try {
    const ctx = getContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') void ctx.resume();

    const now = ctx.currentTime;
    const master = effectiveGain(settings.volume);

    for (const note of cueNotes(cue)) {
      const duration = note.duration / 1000;
      const start = now + (note.delay ?? 0) / 1000;
      const end = start + duration;
      const { attack, release } = noteEnvelope(duration);

      const oscillator = ctx.createOscillator();
      const envelope = ctx.createGain();

      oscillator.type = CUE_WAVEFORM;
      oscillator.frequency.setValueAtTime(note.frequency, start);

      // La rampa non è cosmetica: un'onda che parte e finisce di netto produce
      // un clic che, ripetuto centinaia di volte, diventa il suono che l'operatore
      // ricorda — invece del segnale. Ma tra la salita e la discesa la nota
      // **tiene** il picco: è lì che sta l'energia.
      const peak = master * (note.gain ?? 1);
      envelope.gain.setValueAtTime(0, start);
      envelope.gain.linearRampToValueAtTime(peak, start + attack);
      envelope.gain.setValueAtTime(peak, start + release);
      envelope.gain.linearRampToValueAtTime(0, end);

      oscillator.connect(envelope);
      envelope.connect(getBus(ctx));
      oscillator.start(start);
      oscillator.stop(end);
    }
  } catch {
    // Un suono mancato non deve mai far fallire l'inserimento.
  }
}
